import React, { FC } from "react";
import styled from "styled-components";
import { layout, media } from "@literati/public-ui-shared-react";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import AuthForm from "components/authenticate/AuthForm";
import useAuthForm from "components/authenticate/useAuthForm";
import { AuthValues } from "components/authenticate/authTypes";

const StyledStepLayout = styled(StepLayout)`
  .${StepLayoutClassName.FORM} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 0.5rem 0.25rem;
    max-width: unset;
    overflow-x: hidden;
  }

  .${StepLayoutClassName.HEADING} {
    ${layout.contentSpacing}
    font-size: 1.75rem;
  }

  .${StepLayoutClassName.SUBHEADING} {
    ${layout.contentSpacing}
    ${media.sm} {
      white-space: pre-wrap;
    }
  }
`;

const StyledSection = styled.section`
  .${layout.contentSpacing}
  margin: 2rem 0 0;
  max-width: 25rem;
`;

export type StepLayoutPropsAuth = Omit<
  StepLayoutProps<AuthValues>,
  "asForm" | "children"
>;

export interface StepAuthProps extends StepLayoutPropsAuth {
  onAuthenticationSuccess?: () => void;
}

export const StepAuth = ({
  onAuthenticationSuccess,
  stepFooterProps,
  ...stepLayoutProps
}: StepAuthProps) => {
  const initialValues: Partial<AuthValues> = {};

  const {
    isDummyAccountForm,
    authFormProps,
    submitButtonProps: nextButtonProps,
  } = useAuthForm({ initialFormAction: "createDummyAccount" });

  return (
    <StyledStepLayout<FC<StepLayoutProps<AuthValues>>>
      // multiple self-contained forms on page, so we don't use the asForm args
      asForm={false}
      bgColor="white"
      heading="Let’s get started"
      stepFooterProps={{
        nextButtonProps,
        ...stepFooterProps,
      }}
      subheading={
        isDummyAccountForm
          ? "Add an email address to save your reader's preferences\nand see how you can help them grow."
          : null
      }
      {...stepLayoutProps}
    >
      <StyledSection>
        <AuthForm
          flow="dummy-account"
          initialFormAction="createDummyAccount"
          initialValues={initialValues}
          onSocialLoginSuccess={onAuthenticationSuccess}
          onSuccess={onAuthenticationSuccess}
          submitButtonProps={nextButtonProps}
          {...authFormProps}
        />
      </StyledSection>
    </StyledStepLayout>
  );
};

export default StepAuth;
